// P0.5 — Conversation State Reconstruction
//
// Derives conversation-depth state DIRECTLY from a pasted conversation history
// so a deep conversation arrives at the backend as a deep conversation, instead
// of relying on UI continuation counters that reset to 0 on first paste.
//
// This is a STATE-INGESTION utility only. It produces the same payload fields
// the system already consumes (exchange_count, reciprocity_count, reply_lengths)
// — it does NOT change any AI behavior, prompts, ranking, or engine logic.

const MINE_RE = /^(me|you|i|my reply|you sent|sent)\s*[:\-]\s*/i;
const THEIRS_RE = /^(her|him|them|she|he|their reply|they|match)\s*[:\-]\s*/i;
const GENERIC_LABEL_RE = /^([A-Za-z][\w .'-]{0,24})\s*[:\-]\s*(.*)$/;
const LAUGH_RE = /\b(lol|haha|lmao|hehe|rofl|lmfao)\b|😂|🤣|😭|😅|💀/i;
const EMOJI_RE = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/u;
const OPINION_RE = /\b(i think|i feel|i love|i hate|honestly|actually|imo|tbh|i'd say|in my opinion|i believe|i prefer)\b/i;
const STORY_RE = /\b(i (once|went|got|saw|did|tried|remember|used to)|last (night|week|time)|one time|reminds me|so basically)\b/i;
const CHALLENGE_RE = /\b(prove it|prove|bet|dare|convince|try me|impress me|we'll see|doubt it|or what|says who)\b/i;

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// --- Timestamp normalization (WhatsApp / iMessage / Messenger exports) ---
// Strips a LEADING timestamp/metadata prefix so a timestamped export reconstructs
// identically to the same chat without timestamps. Only clear timestamp prefixes
// are removed; conversation content is never touched.
//   "[10:32 AM] Me: Hey"            -> "Me: Hey"
//   "[2026-06-12, 14:03] Sarah: Hi" -> "Sarah: Hi"
//   "12/06/2026, 14:03 - Me: Hey"   -> "Me: Hey"
//   "2026-06-12 14:03:22 - Sarah:Hi"-> "Sarah:Hi"
//   "Jun 12, 2026 8:41 PM - Me: Hey"-> "Me: Hey"
// A non-timestamp bracket like "[RESTART]" / "[Context: ...]" is NOT stripped here
// (it is still handled by the bracketed-meta skip in parseConversationTurns).
const TS_BRACKET_RE = /^\[[\d\s:/.,\-]*(?:[ap]\.?m\.?)?\]\s*/i;
const TS_DATE_DASH_RE =
  /^(?:\d{1,4}[/\-.]\d{1,2}[/\-.]\d{1,4}|[A-Za-z]{3,9}\.?\s+\d{1,2},?\s+\d{2,4}),?\s*(?:\d{1,2}:\d{2}(?::\d{2})?)?\s*(?:[ap]\.?m\.?)?\s*[-\u2013\u2014]\s+/i;

const stripTimestampPrefix = (line) => {
  let out = line;
  if (TS_BRACKET_RE.test(out)) out = out.replace(TS_BRACKET_RE, "");
  if (TS_DATE_DASH_RE.test(out)) out = out.replace(TS_DATE_DASH_RE, "");
  return out.trim();
};

// Normalize an entire pasted history by removing leading timestamp prefixes from
// each line. Lines WITHOUT a timestamp prefix are returned byte-for-byte unchanged,
// so non-timestamped pastes behave exactly as before (zero behavior change).
export const normalizeConversationHistory = (history) => {
  if (!history) return history;
  return history
    .split("\n")
    .map((line) => {
      const stripped = stripTimestampPrefix(line);
      return stripped !== line.trim() ? stripped : line;
    })
    .join("\n");
};

// Split a pasted conversation into ordered turns: [{ speaker: 'me'|'them', text }]
export const parseConversationTurns = (history, contactName) => {
  if (!history || !history.trim()) return [];
  const lines = history
    .split("\n")
    .map((l) => stripTimestampPrefix(l.trim()))
    .filter(Boolean);
  const nameRe =
    contactName && contactName !== "New Chat" && contactName.trim()
      ? new RegExp("^" + escapeRe(contactName.trim()) + "\\s*[:\\-]\\s*", "i")
      : null;

  const turns = [];
  let lastSpeaker = null;
  for (const line of lines) {
    // Skip genuine bracketed meta lines (e.g. "[RESTART ...]", "[Context: ...]").
    // A leading *timestamp* bracket (e.g. "[10:32 AM]") was already removed above.
    if (/^\[/.test(line)) continue;
    let speaker = null;
    let text = line;
    if (MINE_RE.test(line)) {
      speaker = "me";
      text = line.replace(MINE_RE, "");
    } else if (THEIRS_RE.test(line)) {
      speaker = "them";
      text = line.replace(THEIRS_RE, "");
    } else if (nameRe && nameRe.test(line)) {
      speaker = "them";
      text = line.replace(nameRe, "");
    } else {
      const m = line.match(GENERIC_LABEL_RE);
      if (m) {
        const label = m[1].trim().toLowerCase();
        speaker = /^(me|you|i|sent)$/.test(label) ? "me" : "them";
        text = m[2];
      } else {
        // Unlabeled line — alternate from the previous speaker (default to them).
        speaker = lastSpeaker === "them" ? "me" : "them";
      }
    }
    turns.push({ speaker, text: text.trim() });
    lastSpeaker = speaker;
  }
  return turns;
};

// Evidence-based participation: does THIS incoming message show real engagement?
// (Measures participation, not punctuation.)
const isParticipatory = (text) => {
  if (!text) return false;
  const t = text.trim();
  const lower = t.toLowerCase();
  const wordCount = lower.replace(/[^\w\s?]/g, "").split(/\s+/).filter(Boolean).length;
  const hasQuestion = t.includes("?");
  const hasLaugh = LAUGH_RE.test(lower);
  const hasEmoji = EMOJI_RE.test(t);
  const hasOpinion = OPINION_RE.test(lower);
  const hasStory = STORY_RE.test(lower);
  const hasChallenge = CHALLENGE_RE.test(lower);
  return (
    hasQuestion ||
    hasOpinion ||
    hasStory ||
    hasChallenge ||
    wordCount >= 5 ||
    ((hasLaugh || hasEmoji) && wordCount >= 2)
  );
};

const wordsIn = (text) =>
  (text || "").trim().replace(/[^\w\s]/g, "").split(/\s+/).filter(Boolean).length;

// Reconstruct conversation-depth state from a pasted conversation history.
// Returns the payload-ready counters + engagement diagnostics.
export const reconstructStateFromHistory = (history, contactName) => {
  const turns = parseConversationTurns(history, contactName);
  const theirTurns = turns.filter((t) => t.speaker === "them" && t.text);
  const myTurns = turns.filter((t) => t.speaker === "me" && t.text);

  // Per-turn conversational fields derived DIRECTLY from the transcript (source of truth) so a
  // fresh generation never has to trust stale React memory. null when not reconstructable.
  const lastHerMessage = theirTurns.length ? theirTurns[theirTurns.length - 1].text : null;
  const prevHerMessage = theirTurns.length > 1 ? theirTurns[theirTurns.length - 2].text : null;
  const lastAiReply = myTurns.length ? myTurns[myTurns.length - 1].text : null;

  // exchange_count = number of completed exchanges ≈ number of incoming turns.
  const exchangeCount = theirTurns.length;

  // reply_lengths = word counts of the last 5 incoming messages.
  const replyLengths = theirTurns.slice(-5).map((t) => wordsIn(t.text));

  // reciprocity_count = number of incoming messages showing real participation.
  const reciprocityCount = theirTurns.reduce(
    (n, t) => n + (isParticipatory(t.text) ? 1 : 0),
    0
  );

  // Engagement diagnostics (depth signals).
  const allLens = theirTurns.map((t) => wordsIn(t.text));
  const avgReplyLength = allLens.length
    ? Math.round((allLens.reduce((a, b) => a + b, 0) / allLens.length) * 10) / 10
    : 0;
  const playfulness = theirTurns.filter((t) => LAUGH_RE.test(t.text) || EMOJI_RE.test(t.text)).length;
  const warmth = theirTurns.filter((t) => OPINION_RE.test(t.text) || STORY_RE.test(t.text)).length;

  return {
    exchangeCount,
    reciprocityCount,
    replyLengths,
    avgReplyLength,
    lastHerMessage,
    prevHerMessage,
    lastAiReply,
    depthSignals: {
      messageCount: turns.length,
      incomingCount: theirTurns.length,
      outgoingCount: myTurns.length,
      avgReplyLength,
      playfulness,
      warmth,
    },
  };
};
